import React, {
	useState,
	useMemo,
	useEffect,
	useLayoutEffect,
	useRef,
	useCallback,
	type FC,
} from 'react';
import pick from 'lodash/pick';
import DropdownMenu, { DropdownItemGroup, type OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	ForgeAppsContainer,
	useForgeApps,
	useIsAnyAppLoading,
} from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common/src/ui/error/index.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
	FireUiAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { usePluggableItemsQuery } from '../services/query/index.tsx';
import { ACTIONS_MIN_WIDTH, CONTAINER_MIN_WIDTH } from './constants.tsx';
import { DropdownItemWithApps as DropdownItem } from './dropdown-item/index.tsx';
import { Empty } from './empty/index.tsx';
import { PluggableItemsFilterButton as FilterButton } from './filter-button/index.tsx';
import { IssueNavigatorActionRuntimeContainer } from './forge-runtime-container/index.tsx';
import { isInOrIsIFrame } from './utils.tsx';

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
export const closeDialog = (): void => window.connectHost?.dialog?.close();
export const getOpenDialog = (): HTMLIFrameElement | undefined | null => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const topEl = window.AJS?.LayerManager?.global?.getTopLayer();
	if (topEl == null) {
		return null;
	}
	return topEl.find('iframe')?.[0];
};

export const useModalDialogCleanupEffect = (): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useEffect(
		() => () => {
			try {
				const dialogBeforeClose = getOpenDialog();
				closeDialog();
				const dialogNow = getOpenDialog();

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					dialogBeforeClose && dialogNow ? 'iframe not closed' : 'iframe closed',
					'apps',
					{
						// Match the unique key part separated by a final __ delimination
						uniqueKey: dialogBeforeClose?.id.match(/.*(?=__)/)?.[0] ?? dialogBeforeClose?.id,
						uniqueOpenKey: dialogNow?.id.match(/.*(?=__)/)?.[0] ?? dialogNow?.id,
					},
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'issue-navigator-action-pluggable-items.ui.useModalDialogCleanupEffect',
						packageName: 'jiraIssueNavigatorActionPluggableItems',
						teamName: 'empanada',
					},
				});
			}
		},
		[createAnalyticsEvent],
	);
};

export const useRegisterModalDialogAnalytics: () => () => void = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const iFrameEstablishedCallback = useCallback(
		(payload: { extension: { options: Record<string, unknown> } }) => {
			if (payload.extension.options.moduleType === 'dialogs') {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'iframe established',
					'apps',
					pick(payload.extension.options, [
						'addon_key',
						'isDialog',
						'dialogId',
						'isFullPage',
						'uniqueKey',
						'origin',
					]),
				);
			}
		},
		[createAnalyticsEvent],
	);
	const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>();
	const registerListeners = useCallback(() => {
		// Connect doesn't seem to have a way to notify when it has been registered, so we have to resort to polling
		timeoutIdRef.current = setTimeout(() => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			if (!window.connectHost) {
				registerListeners();
			} else {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.connectHost.onIframeEstablished(iFrameEstablishedCallback);
			}
		}, 1000);
	}, [iFrameEstablishedCallback]);

	useLayoutEffect(
		() => () => {
			clearTimeout(timeoutIdRef.current);
			// @ts-expect-error - TS2339 - Unsafe use of Connect's internal API
			if (iFrameEstablishedCallback._wrapper != null) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.connectHost?.offIframeEstablished(iFrameEstablishedCallback);
			}
			timeoutIdRef.current = undefined;
		},
		[iFrameEstablishedCallback, registerListeners],
	);

	return () => {
		if (timeoutIdRef.current === undefined) {
			registerListeners();
		}
	};
};

export const useInlineDialogZIndexOverrideLayoutEffect = (): void => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const { current: styleNode } = useRef<HTMLStyleElement>(document.createElement('style'));
	useLayoutEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.head?.appendChild(styleNode);
		styleNode.setAttribute('data-nin-pluggable-items', 'css');
		styleNode.sheet?.insertRule(
			'div.aui-inline-dialog[id^="inline-dialog-webitem-"]{z-index:401}',
			0,
		);
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.head?.removeChild(styleNode);
		};
	}, [styleNode]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PluggableItems: FC<Record<any, any>> = () => {
	const [fetchData, { loading, error, data }] = usePluggableItemsQuery();
	const [isAnyAppLoading] = useIsAnyAppLoading();
	const [, { closeApps }] = useForgeApps();
	const totalCount = data?.length ?? 0;
	const [isOpen, setIsOpen] = useState(false);
	const allIds: string[] = useMemo(() => data?.map((item) => item?.id) ?? [], [data]);
	const registerModalDialogAnalytics = useRegisterModalDialogAnalytics();
	useModalDialogCleanupEffect();
	useInlineDialogZIndexOverrideLayoutEffect();

	// Close the dropdown when all Forge apps are loaded.
	// In practice it will be a single app at a time, but we can't rely on that.
	useEffect(() => {
		if (!isAnyAppLoading) {
			setIsOpen(false);
		}
	}, [isAnyAppLoading]);

	// Close all Forge apps when the data is re-fetched
	useEffect(() => {
		(async () => {
			await closeApps();
		})();
	}, [data, closeApps]);

	const onOpenChange = useCallback(
		(attrs: OnOpenChangeArgs) => {
			// Ignore events originating from a matching iframe or an element inside an iframe
			const eventTarget = attrs.event?.target;

			if (
				eventTarget &&
				eventTarget instanceof HTMLElement &&
				isInOrIsIFrame(eventTarget, allIds)
			) {
				return;
			}

			if (attrs.isOpen) {
				if (!loading) {
					fetchData(error);
				}

				// Only setup analytics when the dialog is opened
				registerModalDialogAnalytics();
			}

			setIsOpen(attrs.isOpen);
		},
		[allIds, error, fetchData, loading, registerModalDialogAnalytics],
	);

	return (
		<>
			<DropdownMenu
				isLoading={loading}
				testId="issue-navigator-action-pluggable-items.ui.filter-button"
				trigger={({ triggerRef, ...props }) => <FilterButton {...props} ref={triggerRef} />}
				shouldFlip
				placement="bottom-end"
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				spacing="compact"
				shouldRenderToParent
			>
				<Box xcss={containerStyles}>
					{!error && totalCount > 0 ? (
						<DropdownItemGroup>
							{data?.map((item) => (
								<DropdownItem key={item.id} item={item} totalCount={totalCount} />
							))}
						</DropdownItemGroup>
					) : (
						<Box xcss={maxWidthContainerStyles} role="menuitem">
							{error ? (
								<>
									<ErrorState pageAction="apps" onRetry={() => fetchData(true)} />
									<FireUiAnalytics
										action="viewed"
										actionSubject="errorDropdown"
										actionSubjectId="issueNavigatorActionsPluggableItems"
									/>
								</>
							) : (
								<Empty />
							)}
						</Box>
					)}
				</Box>
			</DropdownMenu>
			<IssueNavigatorActionRuntimeContainer />
		</>
	);
};

const PlagguebleItemsWithForge = () => (
	<ForgeAppsContainer scope="issue-navigator-action">
		<PluggableItems />
	</ForgeAppsContainer>
);

export default PlagguebleItemsWithForge;

const containerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${ACTIONS_MIN_WIDTH}px`,
});

const maxWidthContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${CONTAINER_MIN_WIDTH}px`,
});
